import { NodeOrEdge } from "@hex-insights/app-modules";
import { asArray, compareObjects, compareStringsAsc, getObjectValues, TypeOrArray } from "@hex-insights/core";
import {
	asNode,
	BillingAdjustment,
	Contract,
	ContractInstallment,
	ContractLineItem,
	ContractLineItemPricingFragment,
	PaymentLineItem,
} from "../../../Utilities";
import { ContractFormValues } from "../../FormUtilities";
import * as ModelUtilities from "../_General";
import * as ContractInstallmentUtilities from "../ContractInstallment";
import * as ContractLineItemUtilities from "../ContractLineItem";

export enum ContractStatus {
	Draft = "Draft",
	Approved = "Approved",
	Active = "Active",
	Void = "Void",
}

export function mergePaymentProgresses(progresses: PaymentProgressInformation[]) {
	const merged: PaymentProgressInformation = {
		totalPrice: 0,
		amountPaid: 0,
		amountDue: 0,
		amountOverdue: 0,
		amountUpcoming: 0,
	};

	for (let i = 0; i < progresses.length; i++) {
		const progress = progresses[i];
		merged.totalPrice += progress.totalPrice;
		merged.amountPaid += progress.amountPaid;
		merged.amountDue += progress.amountDue;
		merged.amountOverdue += progress.amountOverdue;
		merged.amountUpcoming += progress.amountUpcoming;
	}

	return merged;
}

export type PaymentProgressInformation = {
	totalPrice: number;
	amountPaid: number;
	amountOverdue: number;
	amountDue: number;
	amountUpcoming: number;
};

export const blankPaymentProgressInformation: PaymentProgressInformation = {
	totalPrice: 0,
	amountPaid: 0,
	amountOverdue: 0,
	amountDue: 0,
	amountUpcoming: 0,
};

export function calculatePaymentProgress(contract: {
	contractLineItems: (Pick<ContractLineItem, "id"> & ContractLineItemPricingFragment)[];
	contractInstallments: (Pick<ContractInstallment, "id" | "dueDate" | "portion"> & {
		invoices: {
			paymentLineItems: Pick<PaymentLineItem, "amount">[];
			billingAdjustments: Pick<BillingAdjustment, "amount">[];
		}[];
	})[];
}) {
	const progress: PaymentProgressInformation = {
		...blankPaymentProgressInformation,
		totalPrice: calculateTotalPrice(contract),
	};

	const installmentAmounts = calculateContractInstallmentAmounts(contract);

	for (let i = 0; i < contract.contractInstallments.length; i++) {
		const installment = contract.contractInstallments[i];
		const installmentAmount = installmentAmounts[installment.id];
		const amountPaid = ContractInstallmentUtilities.calculateTotalPaid(installment);
		progress.amountPaid += amountPaid;
		const paymentStatus = ContractInstallmentUtilities.getPaymentStatus(installment, installmentAmount, amountPaid);

		switch (paymentStatus) {
			case ModelUtilities.PaymentStatus.Upcoming:
				progress.amountUpcoming += installmentAmount;
				break;
			case ModelUtilities.PaymentStatus.Due:
				progress.amountDue += installmentAmount;
				break;
			case ModelUtilities.PaymentStatus.Overdue:
				progress.amountOverdue += installmentAmount;
				break;
		}
	}

	return progress;
}

export function calculateContractInstallmentAmounts(
	contract: {
		contractLineItems: (Pick<ContractLineItem, "id"> & ContractLineItemPricingFragment)[];
		contractInstallments: Pick<ContractInstallment, "id" | "dueDate" | "portion">[];
	},
	contractInstallmentLineItemPrices?: Record<ContractInstallment["id"], Record<ContractLineItem["id"], number>>,
) {
	const installmentLineItemPrices =
		contractInstallmentLineItemPrices ?? calculateContractInstallmentLineItemPrices(contract);
	const installmentPrices: Record<ContractInstallment["id"], number> = {};
	for (let i = 0; i < contract.contractInstallments.length; i++) {
		const installment = contract.contractInstallments[i];
		installmentPrices[installment.id] = Object.values(installmentLineItemPrices[installment.id]).reduce(
			(a, b) => a + b,
			0,
		);
	}
	return installmentPrices;
}

export function mergeContractInstallments(
	contracts: {
		contractLineItems: (Pick<ContractLineItem, "id"> & ContractLineItemPricingFragment)[];
		contractInstallments: Pick<ContractInstallment, "id" | "dueDate" | "portion">[];
	}[],
) {
	const installmentObj: Record<string, { id: string; dueDate: string; portion: number }> = {};
	for (let i = 0; i < contracts.length; i++) {
		const contract = contracts[i];
		const installmentPrices = calculateContractInstallmentAmounts(contract);
		for (let j = 0; j < contract.contractInstallments.length; j++) {
			const installment = contract.contractInstallments[j];
			const amount = installmentPrices[installment.id];
			if (!installmentObj[installment.dueDate]) {
				installmentObj[installment.dueDate] = {
					id: installment.dueDate,
					dueDate: installment.dueDate,
					portion: 0,
				};
			}
			installmentObj[installment.dueDate].portion += amount;
		}
	}

	return getObjectValues(installmentObj).sort(compareObjects((e) => e.dueDate, compareStringsAsc()));
}

export function calculateContractInstallmentLineItemPrices(contract: {
	contractLineItems: (Pick<ContractLineItem, "id"> & ContractLineItemPricingFragment)[];
	contractInstallments: Pick<ContractInstallment, "id" | "dueDate" | "portion">[];
}) {
	const lineItemPrices: Record<ContractLineItem["id"], number> = {};
	for (let i = 0; i < contract.contractLineItems.length; i++) {
		const lineItem = contract.contractLineItems[i];
		const lineItemTotalPrice = ContractLineItemUtilities.calculateTotalAmount(lineItem);
		lineItemPrices[lineItem.id] = lineItemTotalPrice;
	}

	let totalPortionRemaining = calculateTotalPortion(contract);

	const installmentLineItemPrices: Record<ContractInstallment["id"], Record<ContractLineItem["id"], number>> = {};
	for (let i = 0; i < contract.contractInstallments.length; i++) {
		const installment = contract.contractInstallments[i];
		installmentLineItemPrices[installment.id] = {};
		const installmentFraction = installment.portion / totalPortionRemaining;
		totalPortionRemaining -= installment.portion;
		for (let j = 0; j < contract.contractLineItems.length; j++) {
			const lineItem = contract.contractLineItems[j];
			const price = Math.floor(lineItemPrices[lineItem.id] * installmentFraction);
			installmentLineItemPrices[installment.id][lineItem.id] = price;
			lineItemPrices[lineItem.id] -= price;
		}
	}

	return installmentLineItemPrices;
}

export function calculateTotalPrice(contract: {
	contractLineItems: Parameters<typeof ContractLineItemUtilities.calculateTotalAmount>[0][];
}) {
	let total = 0;
	for (let i = 0; i < contract.contractLineItems.length; i++) {
		const lineItem = contract.contractLineItems[i];
		total += ContractLineItemUtilities.calculateTotalAmount(lineItem);
	}
	return total;
}

export function calculateTotalFormPrice(formValues: Pick<ContractFormValues.Create, "contractLineItems">) {
	let total = 0;
	for (let i = 0; i < formValues.contractLineItems.length; i++) {
		const lineItem = formValues.contractLineItems[i];
		total += ContractLineItemUtilities.calculateTotalFormAmount(lineItem);
	}
	return total;
}

export function calculateTotalPortion(contract: { contractInstallments: Pick<ContractInstallment, "portion">[] }) {
	let total = 0;
	for (let i = 0; i < contract.contractInstallments.length; i++) {
		const installment = contract.contractInstallments[i];
		total += installment.portion ?? 0;
	}
	return total;
}

export function calculateTotalFormPortion(contractInstallments: ContractFormValues.Create["contractInstallments"]) {
	let total = 0;
	for (let i = 0; i < contractInstallments.length; i++) {
		const installment = contractInstallments[i];
		total += installment.portion ?? 0;
	}
	return total;
}

export function precomputeContractInstallments<
	T extends Pick<ContractInstallment, "id" | "dueDate" | "portion"> & {
		invoices: {
			paymentLineItems: Pick<PaymentLineItem, "amount">[];
			billingAdjustments: Pick<BillingAdjustment, "amount">[];
		}[];
	},
>(
	contracts: TypeOrArray<
		NodeOrEdge<{
			contractLineItems: (Pick<ContractLineItem, "id"> & ContractLineItemPricingFragment)[];
			contractInstallments: T[];
		}>
	>,
) {
	const installments: (T & ContractInstallmentUtilities.PrecomputedContractInstallment)[] = [];

	const contractsArray = asArray(contracts);
	for (let i = 0; i < contractsArray.length; i++) {
		const contract = asNode(contractsArray[i]);
		const installmentAmounts = calculateContractInstallmentAmounts(contract);
		for (let j = 0; j < contract.contractInstallments.length; j++) {
			const installment = contract.contractInstallments[j];
			const installmentAmount = installmentAmounts[installment.id];
			const totalPaid = ContractInstallmentUtilities.calculateTotalPaid(installment);
			installments.push({
				...installment,
				index: j,
				totalInstallments: contract.contractInstallments.length,
				totalAmount: installmentAmount,
				totalPaid,
				amountDue: Math.max(installmentAmount - totalPaid, 0),
				paymentStatus: ContractInstallmentUtilities.getPaymentStatus(installment, installmentAmount, totalPaid),
			});
		}
	}

	return installments;
}

export function allowsParentChanges(status: Contract["status"]) {
	return status === "Draft";
}
